import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useState, type MouseEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useHasPermission } from '../../../auth/usePermissions';
import { tokens } from '../../../design';

/**
 * A picker's name / phone as the SERVER sent it (AUT-400). The API redacts
 * without `view_pii`, so there is one string, not a value + redacted pair
 * (`RedactedField` does not fit). The copy button appears only with
 * `view_pii` — copying a masked string is useless. `null` → «—».
 */
const PiiTextInner = ({
  value,
  mono = false,
}: {
  value: string | null;
  mono?: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const canCopy = useHasPermission('view_pii');
  const [copied, setCopied] = useState(false);

  if (value === null || value === '') {
    return (
      <Typography component="span" variant="body2" sx={{ color: tokens.text.tertiary }}>
        {t('pickers.noValue')}
      </Typography>
    );
  }

  const copy = (e: MouseEvent): void => {
    e.stopPropagation();
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Typography component="span" variant="body2" {...(mono ? { fontFamily: 'monospace' } : {})}>
        {value}
      </Typography>
      {canCopy ? (
        <Tooltip title={copied ? t('pii.copied') : t('pii.copy')}>
          <IconButton size="small" onClick={copy} aria-label={t('pii.copy')} sx={{ p: 0.25 }}>
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export const PiiText = observer(PiiTextInner);
